import { useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { s, typeScale } from '@/theme/tokens';
import { Box, Button, Panel, Stack, Text } from '@/ui';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { Section } from '@/ui/layout/Section';
import { PageShell } from '@/ui/primitives/PageShell';
import type { MouseEvent, ReactElement } from 'react';

type NotFoundScreenProps = {
  title?: string;
  body?: string;
  backTo?: string;
  backLabel?: string;
  isAuthenticated?: boolean;
};

const hasTrustedFlowQaReferrer = (): boolean => {
  if (typeof window === 'undefined' || window.history.length <= 1) return false;
  const referrer = window.document.referrer;
  if (!referrer) return false;
  try {
    const referrerUrl = new URL(referrer);
    return (
      referrerUrl.origin === window.location.origin ||
      referrerUrl.hostname === 'flowqa.com' ||
      referrerUrl.hostname.endsWith('.flowqa.com')
    );
  } catch {
    return false;
  }
};

export const NotFoundScreen = ({
  title = 'Page Not Found',
  body = 'The requested page could not be found.',
  backTo,
  backLabel = 'Back',
  isAuthenticated = false,
}: NotFoundScreenProps): ReactElement => {
  const navigate = useNavigate();
  const { setOverrideVisible, clearOverride } = useBreadcrumbs();
  const fallbackBackTo = backTo ?? (isAuthenticated ? '/execution-runs' : '/');
  const canGoBack = backTo === undefined && hasTrustedFlowQaReferrer();

  const handleBack = (event: MouseEvent<HTMLElement>): void => {
    if (!canGoBack) return;
    event.preventDefault();
    void navigate(-1);
  };

  useEffect(() => {
    setOverrideVisible(false);
    return () => {
      clearOverride();
    };
  }, [clearOverride, setOverrideVisible]);

  return (
    <PageShell maxWidth="md" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Section>
        <Box sx={{ width: '100%', maxWidth: '720px', mx: 'auto', pt: s('5xl') }}>
          <Panel
            actions={
              <Button component={RouterLink} to={fallbackBackTo} onClick={handleBack} size="md">
                {backLabel}
              </Button>
            }
            actionsAlign="center"
          >
            <Stack gap="lg" align="center" sx={{ textAlign: 'center' }}>
              <Text
                role="pageTitle"
                component="h1"
                sx={{ fontSize: { xs: typeScale['2xl'], md: typeScale['3xl'] } }}
              >
                {title}
              </Text>
              <Text role="bodyMuted" sx={{ fontSize: typeScale.lg }}>
                {body}
              </Text>
            </Stack>
          </Panel>
        </Box>
      </Section>
    </PageShell>
  );
};
